import { clipboard } from '@teamturing/utils';
import { TrashIcon } from '@primer/octicons-react';
import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';

import useToast from '../../../hooks/useToast';
import {
  MATTeacherConnectionDataTable_matTeacherConnection$data,
  MATTeacherConnectionDataTable_matTeacherConnection$key,
} from '../../../relay/__generated__/MATTeacherConnectionDataTable_matTeacherConnection.graphql';
import DataTable, { DataTableProps, DataTableColumnType } from '../../core/DataTable';
import MutationConfirmIconButton from '../../core/MutationConfirmIconButton';
import Text from '../../core/Text';
import Tooltip from '../../core/Tooltip';

const MATTeacherConnectionDataTable_matTeacherConnection = graphql`
  fragment MATTeacherConnectionDataTable_matTeacherConnection on MATTeacherConnection {
    edges {
      node {
        id
        email
        phoneNumber
      }
    }
  }
`;

const mutation = graphql`
  mutation MATTeacherConnectionDataTable_organizationTeacherDeleteMutation($input: MATOrganizationTeacherDeleteInput!) {
    organizationTeacherDelete(input: $input) {
      id
    }
  }
`;

type Props = {
  matTeacherConnection: MATTeacherConnectionDataTable_matTeacherConnection$key;
  onRefetch?: () => void;
} & Omit<
  DataTableProps<MATTeacherConnectionDataTable_matTeacherConnection$data['edges'][number]['node']>,
  'rows' | 'columns'
>;

const MATTeacherConnectionDataTable = ({
  matTeacherConnection: teacherConnection,
  onRefetch,
  ...props
}: Props) => {
  const { edges } = useFragment(MATTeacherConnectionDataTable_matTeacherConnection, teacherConnection);

  const { toast } = useToast();

  const columns = useMemo(
    (): DataTableColumnType<MATTeacherConnectionDataTable_matTeacherConnection$data['edges'][number]['node']>[] => [
      {
        field: 'email',
        title: 'ID',
        renderValue: ({ email }) => email,
        width: 672,
      },
      {
        field: 'phoneNumber',
        title: '전화번호',
        renderValue: ({ phoneNumber }) => phoneNumber,
        width: 200,
      },
      {
        field: 'id',
        title: '유저 ID',
        renderValue: ({ id }) => (
          <Tooltip
            text={'클릭해서 복사하기'}
            onClick={async () => {
              const isSuccess = await clipboard.writeText(`${id}`);
              if (isSuccess) {
                toast('유저 ID를 복사했어요.', 'success');
              }
            }}
          >
            <Text sx={{ ':hover': { textDecoration: 'underline', cursor: 'pointer' } }}>{'*'.repeat(6)}</Text>
          </Tooltip>
        ),
        width: 100,
      },
      {
        field: 'id',
        title: '삭제',
        renderValue: ({ id }) => (
          <MutationConfirmIconButton
            mutation={mutation}
            input={{ id }}
            icon={TrashIcon}
            color="danger.fg"
            message="정말 삭제하시겠습니까?"
            aria-label="선생님 삭제"
            config={{
              onCompleted: () => {
                toast('선생님 계정이 삭제됐어요', 'success');
                onRefetch?.();
              },
              onError: () => {
                toast('다시 시도해 주세요', 'error');
              },
            }}
          />
        ),
        width: 50,
      },
    ],
    [toast, onRefetch],
  );

  return (
    <DataTable
      rows={edges.map(({ node }) => node).filter((node) => !!node)}
      columns={columns}
      {...props}
    />
  );
};

export default MATTeacherConnectionDataTable;
